import { useCallback } from 'react';

import { setActiveVehicleToSessionStorage } from '@vw-marketing/myvw-portal-web-utils/Utils/Helpers/Helper';

import { applyTextTransformations } from '../../../../../shared/utils/applyTextTransformations';
import { useDealer } from '../../../../../app/providers/DealerContext';
import { useFeatureAppConfig } from '../../../../../shared/hooks/use-feature-app-config';
import { useFeatureServices } from '../../../../../shared/hooks/use-feature-services';
import { useHistory } from '../../../../../shared/hooks/use-history';
import { useMyVwContext } from '../../../../../shared/hooks/use-my-vw-context';
import { useToast } from '../../../../../shared/context/ToastContext';
import { useTrackingManager } from '../../../../../shared/hooks/use-tracking-manager';
import { useUser } from '../../../../../app/providers/UserContext';
import { useVehicle } from '../../../../../app/providers/VehicleContext';
import { Vehicles } from '../../../../../shared/typings/main';
import { AddVehicleViewProps } from '../../add-vehicle/AddVehicleView';

export interface GarageLayerData {
  vehicles: Vehicles[];
  preferredVehicle: any | null;
  translationHandler: (key: string) => string;
  trackingManager: any;
  onVehicleAction: (vehicleId: string, isSelectVehicle: boolean) => Promise<void>;
  onAddVehicle: () => void;
  onNavigateToVehicleManagement: () => void;
}

export function useGarageLayerData({
  onCloseLayer,
  onAddVehicle,
}: {
  onCloseLayer: () => void;
  onAddVehicle: (data: AddVehicleViewProps) => void;
}): GarageLayerData {
  const { authService, translationHandler } = useMyVwContext();
  const { user, refetchUserProfile } = useUser();
  const { userPreferredVehicle, resetPreferredVehicle } = useVehicle();
  const { fetchPreferredDealer, clearPreferredDealer } = useDealer();
  const { showToast } = useToast();
  const { faServicesUrl = '' } = useFeatureAppConfig();
  const { 'locale-service': localeService } = useFeatureServices();
  const localeCode = `${localeService.language}-${localeService.countryCode}`;
  const trackingManager = useTrackingManager();
  const history = useHistory();
  const { vehicles = [] } = user || {};

  const onVehicleAction = useCallback(
    async (vehicleId: string, _isSelectVehicle: boolean): Promise<void> => {
      const selectedVehicle = vehicles.find((v) => v.vehicleId === vehicleId);

      try {
        await authService.setActiveVehicleGqlRequest(vehicleId, localeCode);
        await refetchUserProfile();
        resetPreferredVehicle();
        clearPreferredDealer();

        try {
          const { getUserPreferDealerInfo } =
            await authService.getUserPreferDealerBasicInfoGqlRequest(vehicleId, localeCode);
          if (getUserPreferDealerInfo?.[0]?.dealerId) {
            await fetchPreferredDealer(
              vehicleId,
              selectedVehicle?.vin!,
              getUserPreferDealerInfo[0].dealerId,
            );
          }
        } catch (_err) {
          console.error('Error checking/fetching preferred dealer:', _err);
        }

        setActiveVehicleToSessionStorage(selectedVehicle);

        const raw = translationHandler('garage.success.message.label').replace(
          '{vehicle_information}',
          `${selectedVehicle?.modelYear || ''} ${selectedVehicle?.modelName || ''}`,
        );
        const msg = applyTextTransformations(raw, [
          selectedVehicle?.modelYear || '',
          selectedVehicle?.modelName || '',
        ]);
        showToast(
          Array.isArray(msg) ? msg.map((s) => s.props.children).join('') : msg,
          'Success',
        );

        onCloseLayer();
      } catch (error) {
        const raw = translationHandler('garage.error.message.label').replace(
          '{vehicle_information}',
          `${selectedVehicle?.modelYear || ''} ${selectedVehicle?.modelName || ''}`,
        );
        const msg = applyTextTransformations(raw, [
          selectedVehicle?.modelYear || '',
          selectedVehicle?.modelName || '',
        ]);
        showToast(
          Array.isArray(msg) ? msg.map((s) => s.props.children).join('') : msg,
          'Alert',
        );
      }
    },
    [
      vehicles,
      authService,
      localeCode,
      refetchUserProfile,
      resetPreferredVehicle,
      clearPreferredDealer,
      fetchPreferredDealer,
      translationHandler,
      showToast,
      onCloseLayer,
    ],
  );

  const handleAddVehicle = useCallback(() => {
    onAddVehicle({
      translationHandler,
      trackingManager,
      isCanada: localeService?.countryCode === 'CA',
      baseUrl: faServicesUrl,
    });
  }, [onAddVehicle, translationHandler, trackingManager, localeService, faServicesUrl]);

  const onNavigateToVehicleManagement = useCallback(() => {
    history.push('/vehicle-management');
    onCloseLayer();
  }, [history, onCloseLayer]);

  return {
    vehicles,
    preferredVehicle: userPreferredVehicle ?? null,
    translationHandler,
    trackingManager,
    onVehicleAction,
    onAddVehicle: handleAddVehicle,
    onNavigateToVehicleManagement,
  };
}
